// src/components/Navbar.jsx
// ═══════════════════════════════════════════════════════════════════════════════
// 🧭 NAVBAR - ESTILO eFIT
// Barra de navegación principal con logo y usuario
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";

const ROL_LABEL = {
  superadmin:          '👑 Super Admin',
  admin:               '⚙️ Admin',
  supervisor:          '👔 Supervisor',
  supervisor_pos:      '🛒 Supervisor POS',
  cajero:              '💰 Cajero',
  almacenero:          '📦 Almacenero',
  visualizador:        '👁️ Visualizador',
  contador:            '🧾 Contador',
  auxiliar_contable:   '📋 Aux. Contable',
  supervisor_contable: '📊 Sup. Contable',
};

// Links de la barra central por rol
const getNavLinks = (rol) => {
  const inicio = { to: '/dashboard', icon: '🏠', label: 'Inicio', modulo: 'dashboard' };
  const pos     = { to: '/pos',       icon: '🛒', label: 'POS',      modulo: 'pos' };
  const ventas  = { to: '/ventas',    icon: '📋', label: 'Ventas',   modulo: 'pos' };
  const cajas   = { to: '/gestion-cajas', icon: '🏧', label: 'Cajas', modulo: 'pos' };
  const reportes= { to: '/reportes-pos', icon: '📊', label: 'Reportes', modulo: 'pos' };
  const inv     = { to: '/materiales', icon: '📦', label: 'Inventario', modulo: 'inventario' };

  switch (rol) {
    case 'cajero':
      return [inicio, pos, cajas];
    case 'supervisor_pos':
      return [inicio, pos, ventas, reportes, cajas];
    case 'admin':
    case 'superadmin':
    case 'supervisor':
      return [inicio, pos, inv];
    case 'almacenero':
    case 'visualizador':
      return [inicio, inv];
    default: // contador, auxiliar_contable, supervisor_contable, etc.
      return [inicio];
  }
};

const Navbar = ({ usuario, onLogout }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const rol = usuario?.rol || '';

  const getModuloActivo = () => {
    const path = location.pathname;
    if (path.includes('/pos') || path.includes('/ventas') || path.includes('/caja') || path.includes('/clientes') || path.includes('/reportes-pos')) {
      return 'pos';
    }
    if (path.includes('/material') || path.includes('/entrada') || path.includes('/salida') || path.includes('/requisicion') || path.includes('/inventario')) {
      return 'inventario';
    }
    return 'dashboard';
  };

  const moduloActivo = getModuloActivo();
  const navLinks = getNavLinks(rol);
  const esAdmin = rol === 'admin' || rol === 'superadmin';

  return (
    <nav style={styles.navbar}>
      <div style={styles.container}>
        {/* Logo / Brand */}
        <Link to="/dashboard" style={styles.brand}>
          <img src="/digera.png" alt="eFIT" style={styles.brandLogo} />
          <div style={styles.brandDivider}></div>
          <div style={styles.brandText}>
            <span style={styles.brandName}>Sistema POS</span>
            <span style={styles.brandSub}>eFIT</span>
          </div>
        </Link>

        {/* Navegación Central — dinámica por rol */}
        <div style={styles.navLinks}>
          {navLinks.map(({ to, icon, label, modulo }) => (
            <Link
              key={to}
              to={to}
              style={{
                ...styles.navLink,
                ...(moduloActivo === modulo && location.pathname === to ? styles.navLinkActive :
                    moduloActivo === modulo && to === '/dashboard' ? {} :
                    moduloActivo === modulo && to !== '/dashboard' ? styles.navLinkActive : {}),
              }}
            >
              <span style={styles.navIcon}>{icon}</span>
              <span>{label}</span>
            </Link>
          ))}
        </div>

        {/* Sección Usuario */}
        <div style={styles.userSection}>
          <div
            style={styles.userInfo}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <div style={styles.userAvatar}>
              {usuario?.nombre?.charAt(0).toUpperCase() || "U"}
            </div>
            <div style={styles.userDetails}>
              <span style={styles.userName}>{usuario?.nombre || "Usuario"}</span>
              <span style={styles.userRole}>{ROL_LABEL[rol] || '👤 Usuario'}</span>
            </div>
            <span style={styles.userChevron}>▼</span>
          </div>

          {/* Menú desplegable */}
          {menuOpen && (
            <div style={styles.dropdownMenu}>
              <div style={styles.dropdownHeader}>
                <div style={styles.dropdownAvatar}>
                  {usuario?.nombre?.charAt(0).toUpperCase() || "U"}
                </div>
                <div>
                  <div style={styles.dropdownName}>{usuario?.nombre}</div>
                  <div style={styles.dropdownEmail}>{usuario?.email || 'usuario@sistema.com'}</div>
                </div>
              </div>
              <div style={styles.dropdownDivider}></div>
              <Link to="/dashboard" style={styles.dropdownItem} onClick={() => setMenuOpen(false)}>
                <span>🏠</span> Dashboard
              </Link>
              {/* Clientes — cajero y supervisor_pos también lo necesitan */}
              {['cajero', 'supervisor_pos', 'supervisor', 'admin', 'superadmin'].includes(rol) && (
                <Link to="/clientes" style={styles.dropdownItem} onClick={() => setMenuOpen(false)}>
                  <span>👥</span> Clientes
                </Link>
              )}
              {/* Configuración — solo admin/superadmin */}
              {esAdmin && (
                <Link to="/configuracion" style={styles.dropdownItem} onClick={() => setMenuOpen(false)}>
                  <span>⚙️</span> Configuración
                </Link>
              )}
              <div style={styles.dropdownDivider}></div>
              <button
                style={styles.dropdownLogout}
                onClick={() => { setMenuOpen(false); onLogout(); }}
              >
                <span>🚪</span> Cerrar Sesión
              </button>
            </div>
          )}

          {/* Botón de salir */}
          <button
            style={styles.logoutBtn}
            onClick={onLogout}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.9)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            🚪 Salir
          </button>
        </div>
      </div>

      {/* Overlay para cerrar menú */}
      {menuOpen && (
        <div 
          style={styles.overlay} 
          onClick={() => setMenuOpen(false)}
        ></div>
      )}
    </nav>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🎨 ESTILOS eFIT - PALETA AZUL CORPORATIVA
// ═══════════════════════════════════════════════════════════════════════════════

const styles = {
  navbar: {
    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
    boxShadow: '0 4px 20px rgba(30, 58, 138, 0.3)',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 32px',
    maxWidth: '1800px',
    margin: '0 auto',
  },

  // Brand / Logo
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    cursor: 'pointer',
    textDecoration: 'none',
  },
  brandLogo: {
    height: '60px',
    objectFit: 'contain',
    filter: 'brightness(0) invert(1)',
  },
  brandDivider: {
    width: '2px',
    height: '32px',
    background: 'rgba(255, 255, 255, 0.3)',
    borderRadius: '1px',
  },
  brandText: {
    display: 'flex',
    flexDirection: 'column',
  },
  brandName: {
    fontSize: '16px',
    fontWeight: '700',
    color: 'white',
    lineHeight: '1.2',
  },
  brandSub: {
    fontSize: '11px',
    fontWeight: '600',
    color: 'rgba(255, 255, 255, 0.7)',
    letterSpacing: '2px',
    textTransform: 'uppercase',
  },

  // Navegación Central
  navLinks: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  navLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    color: 'rgba(255, 255, 255, 0.85)',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '600',
    borderRadius: '10px',
    transition: 'all 0.2s ease',
    background: 'transparent',
  },
  navLinkActive: {
    background: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  navIcon: {
    fontSize: '16px',
  },

  // Sección Usuario
  userSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    position: 'relative',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 16px',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: '1px solid rgba(255, 255, 255, 0.15)',
  },
  userAvatar: {
    width: '38px',
    height: '38px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: '700',
    color: 'white',
    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
  },
  userDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  userName: {
    color: 'white',
    fontSize: '14px',
    fontWeight: '600',
  },
  userRole: {
    color: 'rgba(255, 255, 255, 0.75)',
    fontSize: '11px',
    fontWeight: '500',
  },
  userChevron: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: '10px',
    marginLeft: '4px',
  },

  // Dropdown Menu
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    right: '0',
    marginTop: '8px',
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
    minWidth: '260px',
    overflow: 'hidden',
    zIndex: 1001,
    animation: 'fadeInDown 0.2s ease',
  },
  dropdownHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '20px',
    background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)',
  },
  dropdownAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: '700',
    color: 'white',
  },
  dropdownName: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1e293b',
  },
  dropdownEmail: {
    fontSize: '12px',
    color: '#64748b',
    marginTop: '2px',
  },
  dropdownDivider: {
    height: '1px',
    background: '#e2e8f0',
  },
  dropdownItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 20px',
    color: '#475569',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  },
  dropdownLogout: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    padding: '14px 20px',
    background: 'transparent',
    border: 'none',
    color: '#ef4444',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textAlign: 'left',
  },

  // Botón Logout
  logoutBtn: {
    padding: '10px 18px',
    background: 'rgba(255, 255, 255, 0.15)',
    color: 'white',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },

  // Overlay
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'transparent',
    zIndex: 1000,
  },
};

export default Navbar;